// =====================================================================
// WeddingOS – /guest/guestbook
// =====================================================================
// • Jeder Gast kann genau einen Eintrag schreiben
// • Existierender Eintrag wird beim Laden erkannt → Bearbeiten-Modus
// • Realtime dedupliziert per ID
// =====================================================================

import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase, fetchGuestbook, realtime } from "../../services/supabase";
import { loadGuestSession } from "../../lib/guestSession";
import InfoTip from "../../components/InfoTip";
import "../../styles/theme.css";

const GUESTBOOK_STEPS = [
  { title: "Nachricht schreiben", detail: "Tippe deine persoenliche Botschaft ans Brautpaar ins Textfeld." },
  { title: "Absenden", detail: "Mit Eintrag hinterlassen wird deine Nachricht gespeichert und fuer alle sichtbar." },
  { title: "Bearbeiten", detail: "Du kannst deinen Eintrag jederzeit aendern - tippe auf Bearbeiten unter deinem Beitrag." },
  { title: "Andere lesen", detail: "Scrolle nach unten um zu sehen was andere Gaeste geschrieben haben." },
];

// Eigenen Eintrag laden (falls bereits vorhanden)
async function fetchMyEntry(guestId) {
  const { data } = await supabase
    .from("guestbook_entries")
    .select("*")
    .eq("guest_id", guestId)
    .order("created_at", { ascending: false })
    .limit(1);
  return data?.[0] ?? null;
}

// Eintrag aktualisieren
async function updateGuestbookEntry(id, message) {
  const { data, error } = await supabase
    .from("guestbook_entries")
    .update({ message })
    .eq("id", id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export default function GuestGuestbook() {
  const navigate = useNavigate();
  const session  = loadGuestSession();

  const [entries,      setEntries]      = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [myEntry,      setMyEntry]      = useState(null);   // eigener Eintrag aus DB
  const [message,      setMessage]      = useState("");
  const [editMode,     setEditMode]     = useState(false);  // Bearbeiten-Modus
  const [submitting,   setSubmitting]   = useState(false);
  const [error,        setError]        = useState(null);
  const [done,         setDone]         = useState(false);

  // Alle Einträge + eigenen Eintrag laden
  useEffect(() => {
    if (!session?.wedding_id || !session?.guest_id) return;

    Promise.all([
      fetchGuestbook(session.wedding_id),
      fetchMyEntry(session.guest_id),
    ]).then(([all, mine]) => {
      setEntries(all);
      if (mine) {
        setMyEntry(mine);
        setMessage(mine.message); // Vorausfüllen für Bearbeitung
      }
    }).catch(() => {})
      .finally(() => setLoading(false));
  }, [session?.wedding_id, session?.guest_id]);

  // Realtime – dedupliziert
  useEffect(() => {
    if (!session?.wedding_id) return;
    const unsub = realtime.guestbook(session.wedding_id, (payload) => {
      if (payload.eventType === "INSERT") {
        setEntries((prev) => {
          if (prev.some((e) => e.id === payload.new.id)) return prev;
          return [payload.new, ...prev];
        });
      }
      if (payload.eventType === "UPDATE") {
        setEntries((prev) => prev.map((e) => e.id === payload.new.id ? { ...e, ...payload.new } : e));
      }
    });
    return unsub;
  }, [session?.wedding_id]);

  const handleSubmit = useCallback(async () => {
    if (!message.trim()) return;
    if (!session?.guest_id) {
      setError("Sitzung ungültig – bitte neu über den Tisch-Link einloggen.");
      return;
    }
    setSubmitting(true);
    setError(null);

    try {
      if (myEntry) {
        // Bearbeiten
        const updated = await updateGuestbookEntry(myEntry.id, message.trim());
        setMyEntry(updated);
        setEntries((prev) => prev.map((e) => e.id === updated.id ? updated : e));
        setEditMode(false);
      } else {
        // Neu anlegen via RPC
        const { data, error: rpcErr } = await supabase.rpc("submit_guestbook_entry", {
          p_guest_id:  session.guest_id,
          p_message:   message.trim(),
          p_photo_url: "",
        });
        if (rpcErr) throw rpcErr;
        const entry = Array.isArray(data) ? data[0] : data;
        setMyEntry(entry);
        setEntries((prev) => {
          if (prev.some((e) => e.id === entry.id)) return prev;
          return [entry, ...prev];
        });
      }
      setDone(true);
      setTimeout(() => setDone(false), 4000);
    } catch {
      setError("Eintrag konnte nicht gespeichert werden. Bitte nochmal versuchen.");
    } finally {
      setSubmitting(false);
    }
  }, [message, session, myEntry]);

  if (!session) return <NoSession />;

  const charLeft   = 600 - message.length;
  const hasWritten = !!myEntry && !editMode;

  return (
    <div className="wos-shell">
      <PageHeader onBack={() => navigate("/guest")} title="Gästebuch" />

      {/* ── Anleitung ── */}
      {!myEntry && (
        <InfoTip
          short="✍️ Hinterlasse dem Brautpaar eine persönliche Nachricht."
          steps={GUESTBOOK_STEPS}
        />
      )}

      {done && (
        <SuccessBanner message={myEntry && !editMode ? "Eintrag gespeichert ✦" : "Eintrag aktualisiert ✦"} />
      )}

      {/* ── Schreibbereich ── */}
      {hasWritten ? (
        // Eigenen Eintrag anzeigen + Bearbeiten-Button
        <div className="wos-rise" style={{
          padding: "18px 20px", background: "var(--accent-soft)",
          border: "1px solid var(--accent)", borderRadius: "var(--radius-lg)", marginBottom: 24,
        }}>
          <p className="wos-eyebrow" style={{ marginBottom: 6 }}>Dein Eintrag</p>
          <p style={{ margin: 0, fontFamily: "var(--font-display)", fontSize: "1.05rem",
            fontStyle: "italic", lineHeight: 1.6, color: "var(--ink)" }}>
            „{myEntry.message}"
          </p>
          <button
            onClick={() => { setEditMode(true); setMessage(myEntry.message); }}
            style={{
              marginTop: 14, background: "none", border: "1px solid var(--accent)",
              borderRadius: "var(--radius-md)", color: "var(--accent-deep)",
              fontSize: 13, fontWeight: 600, cursor: "pointer",
              fontFamily: "var(--font-body)", padding: "8px 16px",
            }}
          >
            Bearbeiten ✎
          </button>
        </div>
      ) : (
        // Schreib- oder Bearbeitungsformular
        <div className="wos-rise wos-rise-delay-1">
          <div className="wos-card" style={{ marginBottom: 24 }}>
            <p className="wos-eyebrow" style={{ marginBottom: 6 }}>
              {editMode ? "Eintrag bearbeiten" : "Dein Eintrag ans Brautpaar"}
            </p>
            {!editMode && (
              <p className="wos-meta" style={{ marginBottom: 20, fontSize: 14 }}>
                Was möchtest du dem Brautpaar auf den Weg mitgeben?
              </p>
            )}

            <div className="wos-field">
              <label className="wos-label" htmlFor="gb-message">Deine Nachricht</label>
              <textarea
                id="gb-message" className="wos-textarea" rows={5}
                placeholder="Herzlichen Glückwunsch … alles Gute …"
                value={message}
                onChange={(e) => setMessage(e.target.value.slice(0, 600))}
                style={{ resize: "none" }}
              />
              <p style={{ textAlign: "right", margin: "4px 0 0", fontSize: 12,
                color: charLeft < 60 ? "var(--accent)" : "var(--ink-subtle)" }}>
                {charLeft} Zeichen übrig
              </p>
            </div>

            {error && <div className="wos-error">{error}</div>}

            <div style={{ display: "flex", gap: 8 }}>
              <button
                className="wos-btn wos-btn-primary"
                onClick={handleSubmit}
                disabled={submitting || !message.trim()}
                style={{ flex: 1 }}
              >
                {submitting
                  ? <><span className="wos-spinner" />Wird gespeichert …</>
                  : editMode ? "Änderungen speichern ✦" : "Eintrag hinterlassen ✦"}
              </button>
              {editMode && (
                <button
                  className="wos-btn wos-btn-ghost"
                  onClick={() => { setEditMode(false); setMessage(myEntry?.message ?? ""); }}
                  disabled={submitting}
                  style={{ flex: "0 0 auto", padding: "16px 18px" }}
                >
                  Abbrechen
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Chronik ── */}
      {loading ? (
        <EntriesSkeleton />
      ) : entries.length > 0 ? (
        <EntriesChronik entries={entries} myGuestId={session.guest_id} />
      ) : (
        <EmptyState />
      )}
    </div>
  );
}

// ── Chronik ──────────────────────────────────────────────────────────

function EntriesChronik({ entries, myGuestId }) {
  return (
    <div className="wos-rise" style={{ marginTop: 12 }}>
      <div className="wos-divider" style={{ marginBottom: 20 }}>
        <span style={{ fontSize: 10, letterSpacing: "0.25em", color: "var(--ink-subtle)", whiteSpace: "nowrap" }}>
          ALLE EINTRÄGE
        </span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {entries.map((entry, i) => (
          <EntryCard key={entry.id} entry={entry} isOwn={entry.guest_id === myGuestId} delay={Math.min(i, 4)} />
        ))}
      </div>
    </div>
  );
}

function EntryCard({ entry, isOwn, delay }) {
  const time = formatTime(entry.created_at);
  return (
    <article className="wos-rise" style={{ animationDelay: `${delay * 0.07}s` }}>
      <div style={{
        padding: "18px 20px",
        background: isOwn ? "var(--accent-soft)" : "var(--bg-elevated)",
        border: `1px solid ${isOwn ? "var(--accent)" : "var(--line)"}`,
        borderRadius: "var(--radius-lg)",
        borderTopLeftRadius: isOwn ? "var(--radius-lg)" : "4px",
        borderTopRightRadius: isOwn ? "4px" : "var(--radius-lg)",
      }}>
        <span style={{ display: "block", fontFamily: "var(--font-display)", fontSize: "2.5rem",
          color: isOwn ? "var(--accent)" : "var(--line)", lineHeight: 0.8, marginBottom: 8, userSelect: "none" }}>
          "
        </span>
        <p style={{ margin: 0, fontSize: "1.05rem", lineHeight: 1.6, color: "var(--ink)",
          fontStyle: "italic", fontFamily: "var(--font-display)" }}>
          {entry.message}
        </p>
        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8, marginTop: 14 }}>
          {isOwn && <span style={{ fontSize: 11, color: "var(--accent)", letterSpacing: "0.12em", fontWeight: 600 }}>DU</span>}
          <span style={{ fontSize: 12, color: "var(--ink-subtle)" }}>{time}</span>
        </div>
      </div>
    </article>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────

function EntriesSkeleton() {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 24 }}>
      {[80, 120, 60].map((h, i) => (
        <div key={i} style={{ height: h, borderRadius: "var(--radius-lg)", background: "var(--surface)", opacity: 0.5 }} />
      ))}
    </div>
  );
}

function EmptyState() {
  return (
    <div style={{ textAlign: "center", paddingTop: 24 }}>
      <p style={{ fontFamily: "var(--font-display)", fontSize: "1.3rem", color: "var(--ink-muted)" }}>
        Sei der Erste, der schreibt.
      </p>
    </div>
  );
}

function PageHeader({ onBack, title }) {
  return (
    <header className="wos-rise" style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 28 }}>
      <button onClick={onBack} aria-label="Zurück" style={{
        background: "var(--surface)", border: "1px solid var(--line)", borderRadius: "var(--radius-md)",
        width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 18, color: "var(--ink-muted)", cursor: "pointer", flexShrink: 0,
      }}>←</button>
      <div>
        <p className="wos-eyebrow">Gast-Aktion</p>
        <h1 style={{ fontFamily: "var(--font-display)", fontSize: "1.7rem", marginTop: 2 }}>{title}</h1>
      </div>
    </header>
  );
}

function SuccessBanner({ message }) {
  return (
    <div role="status" style={{ padding: "14px 18px", background: "var(--forest-soft)",
      border: "1px solid var(--forest)", borderRadius: "var(--radius-md)",
      marginBottom: 20, color: "var(--forest)", fontWeight: 500,
      fontSize: 15, animation: "wos-rise 0.3s ease", textAlign: "center" }}>
      {message}
    </div>
  );
}

function NoSession() {
  return (
    <div className="wos-shell" style={{ textAlign: "center", paddingTop: 80 }}>
      <p className="wos-meta">Sitzung abgelaufen. Bitte Einladungslink neu öffnen.</p>
    </div>
  );
}

function formatTime(iso) {
  if (!iso) return "";
  try { return new Intl.DateTimeFormat("de-DE", { hour: "2-digit", minute: "2-digit" }).format(new Date(iso)); }
  catch { return ""; }
}
